import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { signIn, confirmSignIn, signOut } from 'aws-amplify/auth';
import { QRCodeSVG } from 'qrcode.react';
import { ENV } from '../amplify-config';
import { ensureTurnstile } from '../lib/turnstile';
import { useAuth } from '../hooks/useAuth';
import { PasswordInput } from './PasswordInput';
import { safeReturnTo } from '../lib/return-to';
import { clearZoneSession } from '../lib/zones';

/**
 * Staff sign-in form.
 *
 * Per Bot Protection Standard §3.4 and ADR-009:
 *   - Turnstile widget, ENFORCED (1 October 2026). The token rides in
 *     `clientMetadata.turnstileToken` on signIn — Amplify sends it on
 *     InitiateAuth, which is where Cognito runs the staff PreAuthentication
 *     trigger (`lambdas/staff-pre-auth`), and that trigger siteverifies it and
 *     refuses the sign-in on any failure. Until this date the token was sent
 *     and never checked. No token → no submit: the button stays disabled until
 *     the widget issues one, and a spent token (any failed attempt) is cleared
 *     and the widget reset, because Cloudflare tokens are single-use.
 *   - Email + password as the first factor.
 *   - TOTP as the second factor — Cognito returns CONTINUE_SIGN_IN_WITH_TOTP_CODE
 *     when MFA is required, we surface that challenge.
 *
 * Country detection for ADR-009 geo-restriction:
 *   Reads CF-IPCountry from the response of a quick HEAD request to /
 *   (Cloudflare Pages injects the header). Passed to Cognito via
 *   clientMetadata.country on signIn.
 */
type Step = 'credentials' | 'newPassword' | 'totpSetup' | 'totp';

interface FormState {
  email: string;
  password: string;
  newPassword: string;
  totpCode: string;
  turnstileToken: string;
}

const EMPTY_FORM: FormState = {
  email: '',
  password: '',
  newPassword: '',
  totpCode: '',
  turnstileToken: '',
};

interface TotpSetupDetails {
  sharedSecret: string;
  setupUri: string;
}

export function SignIn() {
  const navigate = useNavigate();
  /**
   * ⛔ WHERE TO GO WHEN SIGN-IN SUCCEEDS (UAT fail 1b). Both landing paths below
   * used to be a hard `navigate('/')`, so a staff member bounced here from a deep
   * link — `/admin/users/{sub}/access`, or any other — finished on the dashboard
   * with no trace of what they had asked for. `AuthenticatedRoute` now carries the
   * requested path in router state; `safeReturnTo` validates it and falls back to
   * `/`. See lib/return-to.ts for why this is state rather than `?next=`.
   */
  const location = useLocation();
  const returnTo = safeReturnTo(location.state);
  const { status } = useAuth();
  const [step, setStep] = useState<Step>('credentials');
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [totpSetup, setTotpSetup] = useState<TotpSetupDetails | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);

  // Already-authenticated users skip the form.
  useEffect(() => {
    if (status === 'authenticated') navigate(returnTo, { replace: true });
  }, [status, navigate, returnTo]);

  // ADR-124 A3 — reaching the form signed out means the next session is a NEW sign-in: forget the
  // previous session's working zone and acknowledgement, so it starts from the default zone and the
  // sign-in modal states it again. (Covers an expired session too, not only Sign Out.)
  useEffect(() => {
    if (status === 'unauthenticated') clearZoneSession();
  }, [status]);

  // Mount Turnstile on the credentials step. There is no "no site key" bypass: a
  // production build without one fails deploy.yml's guard, and the dev server falls
  // back to Cloudflare's test key (amplify-config.ts), so the widget always renders.
  useEffect(() => {
    if (step !== 'credentials') return;
    let cancelled = false;
    void ensureTurnstile().then((api) => {
      if (cancelled || !turnstileContainerRef.current) return;
      widgetId.current = api.render(turnstileContainerRef.current, {
        sitekey: ENV.TURNSTILE_SITE_KEY_PORTAL,
        callback: (token: string) => setForm((f) => ({ ...f, turnstileToken: token })),
        'expired-callback': () => setForm((f) => ({ ...f, turnstileToken: '' })),
        'error-callback': () => setForm((f) => ({ ...f, turnstileToken: '' })),
      });
    }).catch(() => {
      if (!cancelled) setError('The security check could not load. Refresh the page to try again.');
    });
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current);
        widgetId.current = null;
      }
    };
  }, [step]);

  async function submitCredentials(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!form.turnstileToken) {
      setError('Please complete the security check.');
      return;
    }
    setBusy(true);
    try {
      const country = await detectCountry();
      const result = await signIn({
        username: form.email,
        password: form.password,
        options: {
          clientMetadata: { country, turnstileToken: form.turnstileToken },
        },
      });
      handleNextStep(result);
    } catch (err) {
      setError(mapCognitoError(err, 'Sign-in failed.'));
      // The token was spent by this attempt (Cloudflare tokens are single-use):
      // clear it and re-challenge, so the retry carries a fresh one.
      setForm((f) => ({ ...f, turnstileToken: '' }));
      try { if (widgetId.current) window.turnstile?.reset(widgetId.current); } catch { /* widget gone */ }
    } finally {
      setBusy(false);
    }
  }

  /**
   * Branches on Cognito's nextStep after any signIn / confirmSignIn call.
   * Staff pool MFA is mandatory (ADR-009), so the post-AdminCreateUser flow
   * is: credentials → newPassword → totpSetup → totp → signed in. Returning
   * users skip to: credentials → totp → signed in.
   */
  function handleNextStep(result: {
    isSignedIn: boolean;
    nextStep: { signInStep: string; totpSetupDetails?: { sharedSecret: string; getSetupUri: (appName: string, account?: string) => URL } };
  }): void {
    if (result.isSignedIn) {
      navigate(returnTo, { replace: true });
      return;
    }
    switch (result.nextStep.signInStep) {
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        setStep('newPassword');
        return;
      case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP': {
        const details = result.nextStep.totpSetupDetails;
        if (details) {
          const uri = details.getSetupUri('NEXA Admin', form.email).toString();
          setTotpSetup({ sharedSecret: details.sharedSecret, setupUri: uri });
        }
        setStep('totpSetup');
        return;
      }
      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        setStep('totp');
        return;
      default:
        setError(`Unexpected next step: ${result.nextStep.signInStep}`);
    }
  }

  async function submitNewPassword(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await confirmSignIn({ challengeResponse: form.newPassword });
      handleNextStep(result);
    } catch (err) {
      setError(mapCognitoError(err, 'Password change failed.'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Reset the in-flight sign-in state and return to the credentials form.
   * `signOut()` clears any Cognito session that may have been partially
   * established during the challenge flow, so the next attempt starts clean
   * (Cognito will also issue a fresh TOTP shared secret).
   */
  async function cancelSignIn(): Promise<void> {
    try {
      await signOut();
    } catch {
      // No active session to clear — fine.
    }
    setTotpSetup(null);
    setForm({ ...EMPTY_FORM, email: form.email });
    setError(null);
    setStep('credentials');
  }

  async function submitTotp(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await confirmSignIn({ challengeResponse: form.totpCode });
      handleNextStep(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Code verification failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="portal-signin">
      {/* BOW-finesse B5 — blue admin logo centered above the heading (mirrors
          the subscriber login's logo placement; the navbar uses the same mark). */}
      <div className="portal-signin-logo-wrap">
        <img className="portal-signin-logo" src="/nexa-logo-admin-blue.png" alt="Nexanomics" />
      </div>
      <h1>NEXA Admin — Sign in</h1>
      {error && <div role="alert" className="portal-error">{error}</div>}

      {step === 'credentials' && (
        <form onSubmit={submitCredentials} aria-busy={busy}>
          <label>
            Email
            <input
              type="email"
              autoComplete="username"
              required
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </label>
          <label>
            Password
            <PasswordInput
              autoComplete="current-password"
              required
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            />
          </label>
          <div ref={turnstileContainerRef} aria-label="Turnstile challenge" />
          <button type="submit" disabled={busy || !form.turnstileToken} className="portal-btn">
            {busy ? 'Signing in…' : 'Continue'}
          </button>
        </form>
      )}

      {step === 'newPassword' && (
        <form onSubmit={submitNewPassword} aria-busy={busy}>
          <p>Choose a permanent password for your account.</p>
          <ul className="portal-signin-password-rules">
            <li>At least 8 characters</li>
            <li>Lowercase letter, uppercase letter, number, and symbol</li>
            <li>Not the temporary password from your email</li>
            <li>Not on the list of known-leaked passwords (Cognito checks against a breached-credentials database — pick something unique to NEXA)</li>
          </ul>
          <label>
            New password
            <PasswordInput
              autoComplete="new-password"
              minLength={8}
              required
              value={form.newPassword}
              onChange={(e) => setForm((f) => ({ ...f, newPassword: e.target.value }))}
            />
          </label>
          <button type="submit" disabled={busy} className="portal-btn">
            {busy ? 'Setting password…' : 'Set password'}
          </button>
          <button
            type="button"
            onClick={() => void cancelSignIn()}
            disabled={busy}
            className="portal-signin-cancel"
          >
            Cancel sign-in
          </button>
        </form>
      )}

      {step === 'totpSetup' && totpSetup && (
        <form onSubmit={submitTotp} aria-busy={busy}>
          <p>
            Set up TOTP two-factor authentication. Open your authenticator app
            on your phone (1Password / Dashlane / Authy / Google Authenticator /
            Microsoft Authenticator) and scan the QR code below. Then enter the
            6-digit code the app shows.
          </p>
          <div className="portal-signin-qr" aria-label="TOTP setup QR code">
            <QRCodeSVG
              value={totpSetup.setupUri}
              size={220}
              level="M"
              includeMargin={true}
            />
          </div>
          <details className="portal-signin-totp-fallback">
            <summary>Can&apos;t scan? Enter the setup key manually</summary>
            <label>
              Account name
              <input type="text" value="NEXA Admin" readOnly />
            </label>
            <label>
              Setup key
              <input
                type="text"
                value={totpSetup.sharedSecret}
                readOnly
                onFocus={(e) => e.target.select()}
              />
            </label>
          </details>
          <label>
            6-digit code from authenticator app
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              value={form.totpCode}
              onChange={(e) => setForm((f) => ({ ...f, totpCode: e.target.value }))}
            />
          </label>
          <button type="submit" disabled={busy} className="portal-btn">
            {busy ? 'Verifying…' : 'Confirm TOTP setup'}
          </button>
          <button
            type="button"
            onClick={() => void cancelSignIn()}
            disabled={busy}
            className="portal-signin-cancel"
          >
            Cancel sign-in
          </button>
        </form>
      )}

      {step === 'totp' && (
        <form onSubmit={submitTotp} aria-busy={busy}>
          <p>Enter the 6-digit code from your authenticator app.</p>
          <label>
            Code
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              value={form.totpCode}
              onChange={(e) => setForm((f) => ({ ...f, totpCode: e.target.value }))}
            />
          </label>
          <button type="submit" disabled={busy} className="portal-btn">
            {busy ? 'Verifying…' : 'Verify'}
          </button>
          <button
            type="button"
            onClick={() => void cancelSignIn()}
            disabled={busy}
            className="portal-signin-cancel"
          >
            Cancel sign-in
          </button>
        </form>
      )}
    </main>
  );
}

/**
 * Detect viewer country via a HEAD request to the page itself — Cloudflare
 * Pages injects the CF-IPCountry header on every response. Returns 'unknown'
 * if the header is unavailable (local dev, or non-CF hosting).
 */
async function detectCountry(): Promise<string> {
  try {
    const resp = await fetch(window.location.origin, { method: 'HEAD' });
    return resp.headers.get('cf-ipcountry') ?? 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Translate Cognito's terse error strings into operator-friendly messages.
 * Cognito's "Provided password cannot be used for security reasons" is the
 * most common confusing case — it fires for two unrelated reasons (reusing
 * the temp password, OR the chosen password matching the leaked-credentials
 * list) and the message gives no hint which.
 */
function mapCognitoError(err: unknown, fallback: string): string {
  const raw = err instanceof Error ? err.message : String(err);
  // A trigger refusal arrives as "PreAuthentication failed with error <msg>." — the
  // trigger's own message is already written for the user, so show only that.
  const preAuth = /PreAuthentication failed with error (.+?)\.?$/.exec(raw);
  if (preAuth?.[1]) return preAuth[1].replace(/\.+$/, '.');
  if (raw.toLowerCase().includes('cannot be used for security reasons')) {
    return (
      'This password cannot be used — either it matches the temporary password from your email, or it appears on a list of known-leaked passwords. Choose a unique password (not one you have used elsewhere).'
    );
  }
  if (raw.toLowerCase().includes('did not conform with policy')) {
    // Cognito's full message lists which rule failed — pass it through.
    return raw;
  }
  return raw || fallback;
}
